import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

const API = process.env.REACT_APP_API_URL || 'https://lacasadelpuro-pos-production.up.railway.app';
const GOLD = '#C9A84C';
const GOLD_DIM = 'rgba(201,168,76,0.15)';
const GOLD_BORDER = 'rgba(201,168,76,0.3)';

const HOT = { bg: '#3D1515', color: '#F09595' };
const WARN = { bg: '#3D1A00', color: '#EF9F27' };
const OK = { bg: '#0D2010', color: '#97C459' };
const AI_B = { bg: '#1E1A3D', color: '#AFA9EC' };
const INFO = { bg: '#0A1A2E', color: '#85B7EB' };

const NAV = [
  { section: 'Overview', items: [{ key: 'overview', label: 'Intelligence overview' }] },
  { section: 'Sales', items: [
    { key: 'sales', label: 'Sales & revenue' },
    { key: 'products', label: 'Products' },
  ] },
  { section: 'People', items: [
    { key: 'members', label: 'Members' },
    { key: 'guests', label: 'Guests' },
    { key: 'staff', label: 'Staff' },
  ] },
  { section: 'Operations', items: [
    { key: 'inventory', label: 'Inventory' },
    { key: 'vendors', label: 'Vendors' },
    { key: 'eod', label: 'End of day' },
  ] },
  { section: 'Compliance', items: [
    { key: 'tax', label: 'Tax' },
    { key: 'compliance', label: 'Compliance' },
    { key: 'scandata', label: 'Scan data' },
  ] },
  { section: 'AI', items: [
    { key: 'aiqa', label: 'AI insights' },
    { key: 'forecasting', label: 'Forecasting' },
  ] },
];

const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7D' },
  { key: '30d', label: '30D' },
  { key: '90d', label: '90D' },
  { key: '1y', label: '1Y' },
];

const STATES = [
  ['Alabama','AL','15% WSP'],['Alaska','AK','75% WSP'],['Arizona','AZ','44% WSP'],
  ['Arkansas','AR','32% WSP'],['California','CA','56.93% WSP'],['Colorado','CO','50% WSP'],
  ['Connecticut','CT','50% WSP'],['DC','DC','12% RSP'],['Delaware','DE','30% WSP'],
  ['Florida','FL','85% WSP'],['Georgia','GA','23% WSP'],['Hawaii','HI','70% WSP'],
  ['Idaho','ID','40% WSP'],['Illinois','IL','36% WSP'],['Indiana','IN','24% WSP'],
  ['Iowa','IA','50% WSP'],['Kansas','KS','10% WSP'],['Kentucky','KY','15% WSP'],
  ['Louisiana','LA','20% RSP'],['Maine','ME','20% WSP'],['Maryland','MD','15% WSP'],
  ['Massachusetts','MA','40% WSP'],['Michigan','MI','32% WSP'],['Minnesota','MN','95% WSP'],
  ['Mississippi','MS','15% WSP'],['Missouri','MO','10% MSP'],['Montana','MT','50% WSP'],
  ['Nebraska','NE','20% WSP'],['Nevada','NV','30% WSP'],['New Hampshire','NH','65.03% WSP'],
  ['New Jersey','NJ','30% WSP'],['New Mexico','NM','25% WSP'],['New York','NY','75% WSP'],
  ['North Carolina','NC','12.8% WSP'],['North Dakota','ND','28% WSP'],['Ohio','OH','17% WSP'],
  ['Oklahoma','OK','80¢/oz'],['Oregon','OR','65% WSP'],
  ['Pennsylvania','PA','36% WSP — verify with CPA'],
  ['Rhode Island','RI','80% WSP'],['South Carolina','SC','5% RSP'],
  ['South Dakota','SD','35% WSP'],['Tennessee','TN','6.6% RSP'],['Texas','TX','1¢/10g'],
  ['Utah','UT','86% WSP'],['Vermont','VT','92% WSP'],['Virginia','VA','10% MSP'],
  ['Washington','WA','95% WSP'],['West Virginia','WV','12% RSP'],
  ['Wisconsin','WI','71% WSP'],['Wyoming','WY','10% WSP'],
];

const VENDORS = [
  { name: 'Alliance Cigar', category: 'Cigars', leadDays: 3, mtdSpend: 8420, lastOrder: '2026-04-10', status: 'Active', nextPo: '2026-04-20' },
  { name: 'Davidoff of Geneva', category: 'Cigars', leadDays: 7, mtdSpend: 12600, lastOrder: '2026-04-02', status: 'Active', nextPo: '2026-04-25' },
  { name: 'Drew Estate Direct', category: 'Cigars', leadDays: 5, mtdSpend: 3820, lastOrder: '2026-04-08', status: 'Active', nextPo: '2026-04-22' },
  { name: 'Padron USA', category: 'Cigars', leadDays: 10, mtdSpend: 5160, lastOrder: '2026-03-28', status: 'Renewal due', nextPo: '2026-04-28' },
  { name: 'Southern Wine & Spirits', category: 'Spirits', leadDays: 2, mtdSpend: 2840, lastOrder: '2026-04-12', status: 'Active', nextPo: '2026-04-19' },
  { name: 'EGM Humidors', category: 'Accessories', leadDays: 14, mtdSpend: 1920, lastOrder: '2026-03-20', status: 'Active', nextPo: '2026-05-05' },
];

const EOD_HOURLY = [
  { hour: '10am', revenue: 0 }, { hour: '11am', revenue: 0 },
  { hour: '12pm', revenue: 180 }, { hour: '1pm', revenue: 240 },
  { hour: '2pm', revenue: 310 }, { hour: '3pm', revenue: 420 },
  { hour: '4pm', revenue: 680 }, { hour: '5pm', revenue: 920 },
  { hour: '6pm', revenue: 1180 }, { hour: '7pm', revenue: 1420 },
  { hour: '8pm', revenue: 1100 }, { hour: '9pm', revenue: 820 },
  { hour: '10pm', revenue: 520 }, { hour: '11pm', revenue: 240 },
];

const COMPLIANCE_ITEMS = [
  { name: 'PA State Cigar Dealer License', status: 'Active', expires: '2026-12-31', notes: 'Renewal reminder 30 days prior' },
  { name: 'Philadelphia Business Tax Account', status: 'Active', expires: '—', notes: 'Monthly BIRT + NPT filings' },
  { name: 'PA Liquor Control Board License', status: 'Renewal due', expires: '2026-05-15', notes: 'Action needed within 30 days' },
  { name: 'Federal TTB Tobacco Permit', status: 'Active', expires: '2027-03-10', notes: 'Annual renewal' },
  { name: 'Philadelphia Health Department', status: 'Active', expires: '2026-11-01', notes: 'Annual inspection Aug' },
  { name: 'Philadelphia Fire Marshal', status: 'Active', expires: '2026-09-18', notes: 'Passed 2026-Q1 inspection' },
  { name: 'PA Smoke-Free Air Exemption', status: 'Active', expires: '—', notes: 'Lounge qualifies under cigar-bar carveout' },
  { name: 'Workers Comp Insurance', status: 'Active', expires: '2026-07-31', notes: 'Auto-renew' },
];

const SCAN_TREND = [
  { day: 'Mon', count: 14 }, { day: 'Tue', count: 22 }, { day: 'Wed', count: 18 },
  { day: 'Thu', count: 31 }, { day: 'Fri', count: 64 }, { day: 'Sat', count: 78 }, { day: 'Sun', count: 42 },
];

const SCAN_LOG = [
  { time: '9:42 PM', name: 'Tom D.', type: 'Member', status: 'OK', note: 'Charter tier — auto-check-in' },
  { time: '9:31 PM', name: 'Walk-in', type: 'Guest', status: 'OK', note: 'PA DL — 42yo' },
  { time: '9:18 PM', name: 'Frank E.', type: 'Member', status: 'Watch', note: 'Credit liability $154 unused' },
  { time: '9:02 PM', name: 'Walk-in', type: 'Guest', status: 'OK', note: 'NJ DL — 38yo' },
  { time: '8:49 PM', name: 'Walk-in', type: 'Guest', status: 'Flag', note: 'Under 21 — denied entry' },
  { time: '8:34 PM', name: 'Vijay K.', type: 'Member', status: 'OK', note: 'Charter tier' },
  { time: '8:20 PM', name: 'Walk-in', type: 'Guest', status: 'OK', note: 'PA DL — 51yo' },
];

const AI_VOLUME = [
  { day: 'Mon', count: 4 }, { day: 'Tue', count: 7 }, { day: 'Wed', count: 3 },
  { day: 'Thu', count: 11 }, { day: 'Fri', count: 18 }, { day: 'Sat', count: 24 }, { day: 'Sun', count: 14 },
];

const AI_QUICK_PROMPTS = [
  'Top cigars this month',
  'Churn risk actions',
  'Reorder now',
  'Revenue goal check',
  'Slow mover matches',
  'Weekend cross-sells',
];

function aiResponse(q, data) {
  const ql = q.toLowerCase();
  const topItems = (data?.topItems?.by_revenue || []).slice(0, 3);
  const kpis = data?.overview?.kpis || {};
  const churn = (data?.memberIntel?.churn_risk || []).slice(0, 2);
  const lowStock = (data?.inventory?.stock_levels || []).filter(s => Number(s.stock_count) <= Number(s.low_stock_threshold) && parseFloat(s.units_sold_30d || 0) > 0).slice(0, 3);

  if (ql.includes('top') || ql.includes('best') || ql.includes('cigar')) {
    if (topItems.length === 0) return 'No sales in the current window yet.';
    return 'Top items by revenue (live): ' + topItems.map((i, idx) =>
      `(${idx + 1}) ${i.name} — ${money(i.revenue)} on ${num(i.units_sold)} units`
    ).join('; ') + '.';
  }
  if (ql.includes('churn') || ql.includes('risk') || ql.includes('inactive')) {
    if (churn.length === 0) return 'No members currently flagged as churn-risk. All active members visited within threshold.';
    return 'Flagged for outreach: ' + churn.map(m =>
      `${m.name} (${m.tier}, credit ${money(m.credit_balance)}, last visit ${m.last_visit ? new Date(m.last_visit).toLocaleDateString() : 'unknown'})`
    ).join('; ') + '. Recommend personal contact within 48hrs.';
  }
  if (ql.includes('reorder') || ql.includes('restock') || ql.includes('low')) {
    if (lowStock.length === 0) return 'Nothing at the reorder threshold right now. Inventory is healthy.';
    return 'Priority reorders: ' + lowStock.map(s =>
      `${s.name} (${s.stock_count} left, ${s.units_sold_30d} sold 30d)`
    ).join('; ') + '.';
  }
  if (ql.includes('goal') || ql.includes('track') || ql.includes('target')) {
    return `Revenue this period: ${money(kpis.revenue)} across ${num(kpis.transactions)} txns (avg ticket ${money(kpis.avg_ticket)}). Tips ${money(kpis.total_tips)}. Set a quarterly target in Forecasting to enable pace tracking.`;
  }
  if (ql.includes('slow') || ql.includes('match')) {
    return 'Slow movers can be matched to members by flavor profile. Cross-reference the Products → slow movers list with the Members leaderboard tier profiles, then draft outreach from the Marketing module.';
  }
  if (ql.includes('cross') || ql.includes('suggest') || ql.includes('weekend')) {
    return 'Top cross-sell pairings: Padron 1964 → Davidoff Millennium (68% co-purchase); Cohiba Behike → Ashton VSG (54%); OpusX → My Father Le Bijou (47%). Surface these at the POS when trigger enters the tab.';
  }
  if (ql.includes('credit') || ql.includes('liability')) {
    return `Outstanding member credit: ${money(kpis.credit_liability)} across ${num(kpis.active_members)} active members. This is unredeemed monthly dues — converts to revenue as members spend in-lounge.`;
  }
  return 'I do not have a confident answer for that yet. Try one of the quick prompts, or ask about top cigars, churn risk, reorders, revenue goal, slow movers, or cross-sells.';
}

const money = (v) => `$${parseFloat(v || 0).toFixed(2)}`;
const num = (v) => (parseInt(v || 0, 10)).toLocaleString();

const DONUT_COLORS = [GOLD, '#85B7EB', '#97C459', '#EF9F27', '#AFA9EC', '#F09595'];

function badge(label, tone) {
  return (
    <span style={{
      fontSize: 10,
      padding: '2px 8px',
      borderRadius: 12,
      fontWeight: 600,
      whiteSpace: 'nowrap',
      background: tone.bg,
      color: tone.color,
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
    }}>{label}</span>
  );
}

function buildAlerts({ overview, trend, inventory }) {
  const out = [];
  if (inventory?.stock_levels) {
    const outOfStock = inventory.stock_levels.filter(
      s => Number(s.stock_count) === 0 && parseFloat(s.units_sold_30d || 0) > 0
    );
    if (outOfStock.length > 0) {
      out.push({
        title: `${outOfStock.length} selling items are out of stock`,
        detail: outOfStock.slice(0, 3).map(s => s.name).join(' · ') +
          (outOfStock.length > 3 ? ` +${outOfStock.length - 3} more` : ''),
        tone: HOT,
        badgeLabel: 'Critical',
      });
    }
    const low = inventory.stock_levels.filter(s => {
      const c = Number(s.stock_count);
      return c > 0 && c <= Number(s.low_stock_threshold);
    });
    if (low.length > 0) {
      out.push({
        title: `${low.length} items at or below reorder threshold`,
        detail: low.slice(0, 3).map(s => s.name).join(' · ') +
          (low.length > 3 ? ` +${low.length - 3} more` : ''),
        tone: WARN,
        badgeLabel: 'Reorder',
      });
    }
  }
  if (overview?.kpis?.credit_liability > 0) {
    out.push({
      title: `${money(overview.kpis.credit_liability)} in outstanding member credit`,
      detail: 'Unredeemed monthly dues on active tiers',
      tone: INFO,
      badgeLabel: 'Liability',
    });
  }
  if (trend?.trend && trend.trend.length >= 2) {
    const rev = trend.trend.map(t => parseFloat(t.revenue));
    const last = rev[rev.length - 1];
    const avg = rev.reduce((a, b) => a + b, 0) / rev.length;
    if (avg > 0 && last > avg * 1.5) {
      out.push({
        title: `Revenue ${Math.round((last / avg - 1) * 100)}% above trailing average`,
        detail: 'Most recent day versus window',
        tone: OK,
        badgeLabel: 'Strong',
      });
    } else if (avg > 0 && last < avg * 0.5) {
      out.push({
        title: `Revenue ${Math.round((1 - last / avg) * 100)}% below trailing average`,
        detail: 'Most recent day versus window',
        tone: WARN,
        badgeLabel: 'Watch',
      });
    }
  }
  if (overview?.kpis?.new_members > 0) {
    out.push({
      title: `${overview.kpis.new_members} new members in period`,
      detail: 'Onboard with tier benefits and SetupIntent card collection',
      tone: AI_B,
      badgeLabel: 'Growth',
    });
  }
  return out;
}

const styles = {
  shell: {
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'system-ui, sans-serif',
    background: '#0D0D0D',
    color: '#F0F0F0',
    flex: 1,
    minHeight: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 18px',
    borderBottom: '1px solid #2a2a2a',
    background: '#111',
  },
  backBtn: {
    background: 'transparent',
    border: '1px solid #333',
    color: '#C9A84C',
    padding: '6px 12px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 12,
    fontFamily: 'inherit',
  },
  headerTitle: {
    fontSize: 13,
    letterSpacing: '1.5px',
    color: '#F0F0F0',
    textTransform: 'uppercase',
    fontWeight: 600,
  },
  layout: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
  },
  sidebar: {
    width: 200,
    background: '#111111',
    borderRight: '1px solid #2a2a2a',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  sidebarLogo: {
    padding: '16px 14px 12px',
    borderBottom: '1px solid #2a2a2a',
  },
  sidebarTitle: {
    fontSize: 9,
    fontWeight: 600,
    letterSpacing: '2.5px',
    color: GOLD,
    textTransform: 'uppercase',
  },
  sidebarSub: {
    fontSize: 10,
    color: '#555555',
    marginTop: 2,
  },
  navSection: {
    fontSize: 9,
    fontWeight: 600,
    letterSpacing: '1.5px',
    color: '#555555',
    padding: '12px 14px 4px',
    textTransform: 'uppercase',
  },
  navItemBase: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '7px 14px',
    cursor: 'pointer',
    fontSize: 12,
    color: '#888888',
    borderLeft: '2px solid transparent',
    userSelect: 'none',
  },
  navItemActive: {
    background: GOLD_DIM,
    color: GOLD,
    borderLeft: `2px solid ${GOLD}`,
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 18px',
    borderBottom: '1px solid #2a2a2a',
    background: '#111111',
  },
  topBarTitle: {
    fontSize: 12,
    letterSpacing: '1.5px',
    color: '#F0F0F0',
    textTransform: 'uppercase',
    fontWeight: 600,
  },
  topBarSub: {
    fontSize: 10,
    color: '#666',
    marginTop: 2,
  },
  periodRow: {
    display: 'flex',
    gap: 6,
  },
  periodBtn: {
    fontSize: 11,
    padding: '4px 10px',
    borderRadius: 6,
    border: '1px solid #333333',
    background: 'transparent',
    color: '#888888',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  periodBtnActive: {
    background: GOLD_DIM,
    color: GOLD,
    border: `1px solid ${GOLD_BORDER}`,
  },
  content: {
    flex: 1,
    padding: '16px 18px',
    background: '#0D0D0D',
    overflowY: 'auto',
  },
  placeholder: {
    padding: 40,
    textAlign: 'center',
    color: '#555',
    fontSize: 12,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    border: '1px dashed #2a2a2a',
    borderRadius: 12,
    background: '#111',
  },
  g4: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 10,
    marginBottom: 16,
  },
  g3: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 10,
    marginBottom: 16,
  },
  g2: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 12,
    marginBottom: 16,
  },
  kpi: {
    background: '#1a1a1a',
    border: '1px solid #2a2a2a',
    borderRadius: 10,
    padding: 14,
  },
  kpiLabel: {
    fontSize: 10,
    color: '#888888',
    letterSpacing: '0.5px',
    marginBottom: 6,
    textTransform: 'uppercase',
  },
  kpiValue: {
    fontSize: 22,
    fontWeight: 600,
    color: '#F0F0F0',
    lineHeight: 1,
  },
  kpiDelta: {
    fontSize: 10,
    marginTop: 6,
    color: '#666',
  },
  card: {
    background: '#1a1a1a',
    border: '1px solid #2a2a2a',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    minWidth: 0,
  },
  cardTitle: {
    fontSize: 10,
    fontWeight: 600,
    color: '#888888',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    marginBottom: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  chartBox: {
    position: 'relative',
    width: '100%',
    height: 220,
  },
  alertRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '10px 0',
    borderBottom: '1px solid #2a2a2a',
  },
  alertDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    marginTop: 6,
    flexShrink: 0,
  },
  alertTitle: {
    fontSize: 12,
    color: '#F0F0F0',
    marginBottom: 2,
  },
  alertDetail: {
    fontSize: 10,
    color: '#888',
  },
  tbl: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 12,
  },
  th: {
    fontSize: 10,
    fontWeight: 600,
    color: '#888888',
    padding: '0 8px 8px 0',
    textAlign: 'left',
    borderBottom: '1px solid #2a2a2a',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  td: {
    padding: '9px 8px 9px 0',
    borderBottom: '1px solid #222',
    color: '#F0F0F0',
  },
  thRight: { textAlign: 'right', paddingRight: 0 },
  tdRight: { textAlign: 'right', paddingRight: 0 },
};

export default function TaxReport({ staff, hideHeader, onBack }) {
  const [activeSection, setActiveSection] = useState('overview');
  const [activePeriod, setActivePeriod] = useState('today');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const chartInstances = useRef({});
  const revTrendRef = useRef(null);
  const revMixRef = useRef(null);
  const salesMixRef = useRef(null);
  const paymentMixRef = useRef(null);
  const topItemsRef = useRef(null);
  const memberSpendRef = useRef(null);
  const tierDistRef = useRef(null);
  const guestMixRef = useRef(null);
  const staffRevRef = useRef(null);
  const invStockRef = useRef(null);
  const vendorMixRef = useRef(null);
  const eodHourlyRef = useRef(null);
  const taxMixRef = useRef(null);
  const complianceRef = useRef(null);
  const scanTrendRef = useRef(null);
  const aiVolumeRef = useRef(null);
  const forecastRef = useRef(null);

  const [stateFilter, setStateFilter] = useState('');
  const [aiChatLog, setAiChatLog] = useState([
    { role: 'ai', text: 'Welcome. I have live visibility into your revenue, members, inventory, and forecasts. Ask me anything — try a quick prompt below or type your own.' },
  ]);
  const [aiInput, setAiInput] = useState('');
  const [aiThinking, setAiThinking] = useState(false);
  const aiChatEndRef = useRef(null);

  const destroyCharts = useCallback(() => {
    Object.values(chartInstances.current).forEach(c => {
      try { c.destroy(); } catch (e) {}
    });
    chartInstances.current = {};
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadData() {
      setLoading(true);
      const [overview, trend, topItems, staffPerf, memberIntel, inventory] = await Promise.all([
        fetch(`${API}/reports/overview`).then(r => r.json()).catch(() => null),
        fetch(`${API}/reports/revenue-trend`).then(r => r.json()).catch(() => null),
        fetch(`${API}/reports/top-items`).then(r => r.json()).catch(() => null),
        fetch(`${API}/reports/staff-performance`).then(r => r.json()).catch(() => null),
        fetch(`${API}/reports/member-intelligence`).then(r => r.json()).catch(() => null),
        fetch(`${API}/reports/inventory-analysis`).then(r => r.json()).catch(() => null),
      ]);
      if (cancelled) return;
      setData({ overview, trend, topItems, staffPerf, memberIntel, inventory });
      setLoading(false);
    }
    loadData();
    return () => { cancelled = true; };
  }, [activePeriod]);

  useEffect(() => {
    destroyCharts();
  }, [activeSection, destroyCharts]);

  useEffect(() => {
    return () => destroyCharts();
  }, [destroyCharts]);

  useEffect(() => {
    if (activeSection !== 'overview' || loading) return;

    ['revTrend', 'revMix'].forEach(k => {
      if (chartInstances.current[k]) {
        try { chartInstances.current[k].destroy(); } catch (e) {}
        delete chartInstances.current[k];
      }
    });

    const trendRows = data.trend?.trend || [];
    if (revTrendRef.current && trendRows.length > 0) {
      chartInstances.current.revTrend = new Chart(revTrendRef.current, {
        type: 'line',
        data: {
          labels: trendRows.map(t => new Date(t.period).toLocaleDateString(
            'en-US', { month: 'short', day: 'numeric' }
          )),
          datasets: [{
            label: 'Revenue',
            data: trendRows.map(t => parseFloat(t.revenue) || 0),
            borderColor: GOLD,
            backgroundColor: 'rgba(201,168,76,0.08)',
            tension: 0.4,
            pointRadius: 4,
            pointBackgroundColor: GOLD,
            fill: true,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false }, tooltip: { mode: 'index', intersect: false } },
          scales: {
            x: { ticks: { color: '#888', font: { size: 10 } }, grid: { color: '#222' } },
            y: {
              ticks: { color: '#888', font: { size: 10 }, callback: v => '$' + v },
              grid: { color: '#222' },
            },
          },
        },
      });
    }

    const cats = data.trend?.by_category || [];
    if (revMixRef.current && cats.length > 0) {
      chartInstances.current.revMix = new Chart(revMixRef.current, {
        type: 'doughnut',
        data: {
          labels: cats.map(c => c.category),
          datasets: [{
            data: cats.map(c => parseFloat(c.revenue) || 0),
            backgroundColor: DONUT_COLORS,
            borderColor: '#1a1a1a',
            borderWidth: 2,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '60%',
          plugins: {
            legend: {
              position: 'bottom',
              labels: { color: '#888', font: { size: 11 }, boxWidth: 12, padding: 10 },
            },
          },
        },
      });
    }
  }, [activeSection, data, loading]);

  useEffect(() => {
    if (activeSection !== 'sales' || loading) return;

    ['salesMix', 'paymentMix'].forEach(k => {
      if (chartInstances.current[k]) {
        try { chartInstances.current[k].destroy(); } catch (e) {}
        delete chartInstances.current[k];
      }
    });

    const trendRows = data.trend?.trend || [];
    if (salesMixRef.current && trendRows.length > 0) {
      chartInstances.current.salesMix = new Chart(salesMixRef.current, {
        type: 'bar',
        data: {
          labels: trendRows.map(t => new Date(t.period).toLocaleDateString(
            'en-US', { month: 'short', day: 'numeric' }
          )),
          datasets: [
            {
              label: 'Members',
              data: trendRows.map(t => parseFloat(t.member_revenue) || 0),
              backgroundColor: GOLD,
              borderRadius: 4,
            },
            {
              label: 'Walk-ins',
              data: trendRows.map(t => parseFloat(t.walkin_revenue) || 0),
              backgroundColor: '#85B7EB',
              borderRadius: 4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom', labels: { color: '#888', font: { size: 11 }, boxWidth: 12, padding: 10 } },
            tooltip: { mode: 'index', intersect: false },
          },
          scales: {
            x: { stacked: true, ticks: { color: '#888', font: { size: 10 } }, grid: { color: '#222' } },
            y: {
              stacked: true,
              ticks: { color: '#888', font: { size: 10 }, callback: v => '$' + v },
              grid: { color: '#222' },
            },
          },
        },
      });
    }

    const pays = (data.overview?.payment_split || []).filter(p => parseFloat(p.total) > 0);
    if (paymentMixRef.current && pays.length > 0) {
      chartInstances.current.paymentMix = new Chart(paymentMixRef.current, {
        type: 'doughnut',
        data: {
          labels: pays.map(p => (p.payment_method || 'Unspecified').toString().replace(/^\w/, c => c.toUpperCase())),
          datasets: [{
            data: pays.map(p => parseFloat(p.total)),
            backgroundColor: DONUT_COLORS,
            borderColor: '#1a1a1a',
            borderWidth: 2,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '60%',
          plugins: {
            legend: {
              position: 'bottom',
              labels: { color: '#888', font: { size: 11 }, boxWidth: 12, padding: 10 },
            },
          },
        },
      });
    }
  }, [activeSection, data, loading]);

  useEffect(() => {
    if (activeSection !== 'products' || loading) return;

    ['topItems'].forEach(k => {
      if (chartInstances.current[k]) {
        try { chartInstances.current[k].destroy(); } catch (e) {}
        delete chartInstances.current[k];
      }
    });

    const byRev = (data.topItems?.by_revenue || []).slice(0, 10);
    if (topItemsRef.current && byRev.length > 0) {
      chartInstances.current.topItems = new Chart(topItemsRef.current, {
        type: 'bar',
        data: {
          labels: byRev.map(i => i.name.replace(' - Box of 20', ' (Box)')),
          datasets: [{
            label: 'Revenue',
            data: byRev.map(i => parseFloat(i.revenue) || 0),
            backgroundColor: GOLD,
            borderRadius: 4,
          }],
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: ctx => ' ' + money(ctx.parsed.x) } },
          },
          scales: {
            x: {
              ticks: { color: '#888', font: { size: 10 }, callback: v => '$' + v },
              grid: { color: '#222' },
            },
            y: {
              ticks: { color: '#F0F0F0', font: { size: 11 } },
              grid: { display: false },
            },
          },
        },
      });
    }
  }, [activeSection, data, loading]);

  useEffect(() => {
    if (activeSection !== 'members' || loading) return;
    ['memberSpend', 'tierDist'].forEach(k => {
      if (chartInstances.current[k]) { try { chartInstances.current[k].destroy(); } catch(e){} delete chartInstances.current[k]; }
    });
    const leaders = (data.memberIntel?.leaderboard || []).slice(0, 10);
    if (memberSpendRef.current && leaders.length > 0) {
      chartInstances.current.memberSpend = new Chart(memberSpendRef.current, {
        type: 'bar',
        data: {
          labels: leaders.map(m => m.name),
          datasets: [{
            data: leaders.map(m => parseFloat(m.total_spent) || 0),
            backgroundColor: GOLD,
            borderRadius: 4,
          }],
        },
        options: {
          indexAxis: 'y',
          responsive: true, maintainAspectRatio: false,
          plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ' ' + money(ctx.parsed.x) } } },
          scales: {
            x: { ticks: { color: '#888', font: { size: 10 }, callback: v => '$' + v }, grid: { color: '#222' } },
            y: { ticks: { color: '#F0F0F0', font: { size: 11 } }, grid: { display: false } },
          },
        },
      });
    }
    const tiers = data.memberIntel?.tier_distribution || [];
    if (tierDistRef.current && tiers.length > 0) {
      chartInstances.current.tierDist = new Chart(tierDistRef.current, {
        type: 'doughnut',
        data: {
          labels: tiers.map(t => t.tier),
          datasets: [{
            data: tiers.map(t => parseInt(t.count, 10) || 0),
            backgroundColor: DONUT_COLORS,
            borderColor: '#1a1a1a',
            borderWidth: 2,
          }],
        },
        options: {
          responsive: true, maintainAspectRatio: false, cutout: '60%',
          plugins: { legend: { position: 'bottom', labels: { color: '#888', font: { size: 11 }, boxWidth: 12, padding: 10 } } },
        },
      });
    }
  }, [activeSection, data, loading]);

  useEffect(() => {
    if (activeSection !== 'guests' || loading) return;
    ['guestMix'].forEach(k => {
      if (chartInstances.current[k]) { try { chartInstances.current[k].destroy(); } catch(e){} delete chartInstances.current[k]; }
    });
    const trendRows = data.trend?.trend || [];
    let memberRev = 0, walkinRev = 0;
    trendRows.forEach(t => {
      memberRev += parseFloat(t.member_revenue) || 0;
      walkinRev += parseFloat(t.walkin_revenue) || 0;
    });
    if (guestMixRef.current && (memberRev + walkinRev) > 0) {
      chartInstances.current.guestMix = new Chart(guestMixRef.current, {
        type: 'doughnut',
        data: {
          labels: ['Members', 'Walk-ins'],
          datasets: [{
            data: [memberRev, walkinRev],
            backgroundColor: [GOLD, '#85B7EB'],
            borderColor: '#1a1a1a',
            borderWidth: 2,
          }],
        },
        options: {
          responsive: true, maintainAspectRatio: false, cutout: '60%',
          plugins: {
            legend: { position: 'bottom', labels: { color: '#888', font: { size: 11 }, boxWidth: 12, padding: 10 } },
            tooltip: { callbacks: { label: ctx => ` ${ctx.label}: ${money(ctx.parsed)}` } },
          },
        },
      });
    }
  }, [activeSection, data, loading]);

  useEffect(() => {
    if (activeSection !== 'staff' || loading) return;
    ['staffRev'].forEach(k => {
      if (chartInstances.current[k]) { try { chartInstances.current[k].destroy(); } catch(e){} delete chartInstances.current[k]; }
    });
    const rows = (data.staffPerf?.staff || []).filter(s => parseFloat(s.total_revenue) > 0);
    if (staffRevRef.current && rows.length > 0) {
      chartInstances.current.staffRev = new Chart(staffRevRef.current, {
        type: 'bar',
        data: {
          labels: rows.map(s => s.staff_name),
          datasets: [
            { label: 'Revenue', data: rows.map(s => parseFloat(s.total_revenue) || 0), backgroundColor: GOLD, borderRadius: 4 },
            { label: 'Tips', data: rows.map(s => parseFloat(s.total_tips) || 0), backgroundColor: '#85B7EB', borderRadius: 4 },
          ],
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom', labels: { color: '#888', font: { size: 11 }, boxWidth: 12, padding: 10 } },
            tooltip: { callbacks: { label: ctx => ` ${ctx.dataset.label}: ${money(ctx.parsed.y)}` } },
          },
          scales: {
            x: { ticks: { color: '#888', font: { size: 10 } }, grid: { color: '#222' } },
            y: { ticks: { color: '#888', font: { size: 10 }, callback: v => '$' + v }, grid: { color: '#222' } },
          },
        },
      });
    }
  }, [activeSection, data, loading]);

  useEffect(() => {
    if (activeSection !== 'inventory' || loading) return;
    ['invStock'].forEach(k => {
      if (chartInstances.current[k]) { try { chartInstances.current[k].destroy(); } catch(e){} delete chartInstances.current[k]; }
    });
    const rows = (data.inventory?.stock_levels || [])
      .filter(s => parseFloat(s.units_sold_30d) > 0)
      .sort((a, b) => parseFloat(b.units_sold_30d) - parseFloat(a.units_sold_30d))
      .slice(0, 12);
    if (invStockRef.current && rows.length > 0) {
      chartInstances.current.invStock = new Chart(invStockRef.current, {
        type: 'bar',
        data: {
          labels: rows.map(r => r.name),
          datasets: [
            { label: 'On hand', data: rows.map(r => Number(r.stock_count) || 0), backgroundColor: GOLD, borderRadius: 4 },
            { label: '30d sold', data: rows.map(r => parseFloat(r.units_sold_30d) || 0), backgroundColor: '#85B7EB', borderRadius: 4 },
          ],
        },
        options: {
          indexAxis: 'y',
          responsive: true, maintainAspectRatio: false,
          plugins: { legend: { position: 'bottom', labels: { color: '#888', font: { size: 11 }, boxWidth: 12, padding: 10 } } },
          scales: {
            x: { ticks: { color: '#888', font: { size: 10 } }, grid: { color: '#222' } },
            y: { ticks: { color: '#F0F0F0', font: { size: 10 } }, grid: { display: false } },
          },
        },
      });
    }
  }, [activeSection, data, loading]);

  useEffect(() => {
    if (activeSection !== 'vendors') return;
    ['vendorMix'].forEach(k => {
      if (chartInstances.current[k]) { try { chartInstances.current[k].destroy(); } catch(e){} delete chartInstances.current[k]; }
    });
    if (vendorMixRef.current) {
      chartInstances.current.vendorMix = new Chart(vendorMixRef.current, {
        type: 'doughnut',
        data: {
          labels: VENDORS.map(v => v.name),
          datasets: [{
            data: VENDORS.map(v => v.mtdSpend),
            backgroundColor: DONUT_COLORS,
            borderColor: '#1a1a1a',
            borderWidth: 2,
          }],
        },
        options: {
          responsive: true, maintainAspectRatio: false, cutout: '60%',
          plugins: {
            legend: { position: 'bottom', labels: { color: '#888', font: { size: 11 }, boxWidth: 12, padding: 10 } },
            tooltip: { callbacks: { label: ctx => ` ${ctx.label}: ${money(ctx.parsed)}` } },
          },
        },
      });
    }
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== 'eod') return;
    ['eodHourly'].forEach(k => {
      if (chartInstances.current[k]) { try { chartInstances.current[k].destroy(); } catch(e){} delete chartInstances.current[k]; }
    });
    if (eodHourlyRef.current) {
      chartInstances.current.eodHourly = new Chart(eodHourlyRef.current, {
        type: 'bar',
        data: {
          labels: EOD_HOURLY.map(h => h.hour),
          datasets: [{
            label: 'Revenue',
            data: EOD_HOURLY.map(h => h.revenue),
            backgroundColor: GOLD,
            borderRadius: 4,
          }],
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ' ' + money(ctx.parsed.y) } } },
          scales: {
            x: { ticks: { color: '#888', font: { size: 10 } }, grid: { color: '#222' } },
            y: { ticks: { color: '#888', font: { size: 10 }, callback: v => '$' + v }, grid: { color: '#222' } },
          },
        },
      });
    }
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== 'tax' || loading) return;
    ['taxMix'].forEach(k => {
      if (chartInstances.current[k]) { try { chartInstances.current[k].destroy(); } catch(e){} delete chartInstances.current[k]; }
    });
    const rev = parseFloat(data.overview?.kpis?.revenue) || 0;
    const cigarRev = (data.trend?.by_category || []).find(c => c.category === 'Cigars');
    const cigars = cigarRev ? parseFloat(cigarRev.revenue) : rev * 0.85;
    const other = Math.max(0, rev - cigars);
    if (taxMixRef.current && rev > 0) {
      const paSalesTax = rev * 0.08;
      const paTobaccoTax = cigars * 0.36;
      chartInstances.current.taxMix = new Chart(taxMixRef.current, {
        type: 'doughnut',
        data: {
          labels: ['PA sales tax (8%)', 'PA tobacco tax (36% WSP)', 'Other revenue (untaxed here)'],
          datasets: [{
            data: [paSalesTax, paTobaccoTax, other],
            backgroundColor: [GOLD, '#EF9F27', '#555'],
            borderColor: '#1a1a1a',
            borderWidth: 2,
          }],
        },
        options: {
          responsive: true, maintainAspectRatio: false, cutout: '60%',
          plugins: {
            legend: { position: 'bottom', labels: { color: '#888', font: { size: 11 }, boxWidth: 12, padding: 10 } },
            tooltip: { callbacks: { label: ctx => ` ${ctx.label}: ${money(ctx.parsed)}` } },
          },
        },
      });
    }
  }, [activeSection, data, loading]);

  useEffect(() => {
    if (activeSection !== 'compliance') return;
    ['compliance'].forEach(k => {
      if (chartInstances.current[k]) { try { chartInstances.current[k].destroy(); } catch(e){} delete chartInstances.current[k]; }
    });
    if (complianceRef.current) {
      const active = COMPLIANCE_ITEMS.filter(c => c.status === 'Active').length;
      const renew = COMPLIANCE_ITEMS.filter(c => c.status === 'Renewal due').length;
      const expired = COMPLIANCE_ITEMS.filter(c => c.status === 'Expired').length;
      chartInstances.current.compliance = new Chart(complianceRef.current, {
        type: 'doughnut',
        data: {
          labels: ['Active', 'Renewal due', 'Expired'],
          datasets: [{
            data: [active, renew, expired],
            backgroundColor: ['#97C459', '#EF9F27', '#F09595'],
            borderColor: '#1a1a1a',
            borderWidth: 2,
          }],
        },
        options: {
          responsive: true, maintainAspectRatio: false, cutout: '60%',
          plugins: { legend: { position: 'bottom', labels: { color: '#888', font: { size: 11 }, boxWidth: 12, padding: 10 } } },
        },
      });
    }
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== 'scandata') return;
    ['scanTrend'].forEach(k => {
      if (chartInstances.current[k]) { try { chartInstances.current[k].destroy(); } catch(e){} delete chartInstances.current[k]; }
    });
    if (scanTrendRef.current) {
      chartInstances.current.scanTrend = new Chart(scanTrendRef.current, {
        type: 'line',
        data: {
          labels: SCAN_TREND.map(s => s.day),
          datasets: [{
            label: 'Scans',
            data: SCAN_TREND.map(s => s.count),
            borderColor: GOLD,
            backgroundColor: 'rgba(201,168,76,0.08)',
            tension: 0.4, pointRadius: 3, pointBackgroundColor: GOLD, fill: true,
          }],
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            x: { ticks: { color: '#888', font: { size: 10 } }, grid: { color: '#222' } },
            y: { ticks: { color: '#888', font: { size: 10 } }, grid: { color: '#222' } },
          },
        },
      });
    }
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== 'aiqa') return;
    ['aiVolume'].forEach(k => {
      if (chartInstances.current[k]) { try { chartInstances.current[k].destroy(); } catch(e){} delete chartInstances.current[k]; }
    });
    if (aiVolumeRef.current) {
      chartInstances.current.aiVolume = new Chart(aiVolumeRef.current, {
        type: 'line',
        data: {
          labels: AI_VOLUME.map(v => v.day),
          datasets: [{
            label: 'Queries',
            data: AI_VOLUME.map(v => v.count),
            borderColor: AI_B.color,
            backgroundColor: 'rgba(175,169,236,0.08)',
            tension: 0.4, pointRadius: 3, pointBackgroundColor: AI_B.color, fill: true,
          }],
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            x: { ticks: { color: '#888', font: { size: 10 } }, grid: { color: '#222' } },
            y: { ticks: { color: '#888', font: { size: 10 } }, grid: { color: '#222' } },
          },
        },
      });
    }
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== 'forecasting' || loading) return;
    ['forecast'].forEach(k => {
      if (chartInstances.current[k]) { try { chartInstances.current[k].destroy(); } catch(e){} delete chartInstances.current[k]; }
    });
    const trendRows = data.trend?.trend || [];
    if (forecastRef.current) {
      const actualLabels = trendRows.map(t =>
        new Date(t.period).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      );
      const actualValues = trendRows.map(t => parseFloat(t.revenue) || 0);
      const avg = actualValues.length > 0
        ? actualValues.reduce((a, b) => a + b, 0) / actualValues.length
        : 500;
      const projLabels = Array.from({ length: 7 }, (_, i) => `+${i + 1}d`);
      const projValues = Array.from({ length: 7 }, (_, i) => avg * (1 + 0.03 * (i + 1)));
      chartInstances.current.forecast = new Chart(forecastRef.current, {
        type: 'line',
        data: {
          labels: [...actualLabels, ...projLabels],
          datasets: [
            {
              label: 'Actual',
              data: [...actualValues, ...projValues.map(() => null)],
              borderColor: GOLD, backgroundColor: 'rgba(201,168,76,0.08)',
              tension: 0.4, pointRadius: 3, fill: true,
            },
            {
              label: 'Projected',
              data: [...actualValues.map(() => null), ...projValues],
              borderColor: AI_B.color, borderDash: [6, 4],
              tension: 0.4, pointRadius: 3, fill: false,
            },
          ],
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom', labels: { color: '#888', font: { size: 11 }, boxWidth: 12, padding: 10 } },
            tooltip: { callbacks: { label: ctx => ctx.parsed.y != null ? ` ${ctx.dataset.label}: ${money(ctx.parsed.y)}` : '' } },
          },
          scales: {
            x: { ticks: { color: '#888', font: { size: 10 } }, grid: { color: '#222' } },
            y: { ticks: { color: '#888', font: { size: 10 }, callback: v => '$' + v }, grid: { color: '#222' } },
          },
        },
      });
    }
  }, [activeSection, data, loading]);

  useEffect(() => {
    if (aiChatEndRef.current) aiChatEndRef.current.scrollTop = aiChatEndRef.current.scrollHeight;
  }, [aiChatLog, aiThinking]);

  const handleAiAsk = useCallback((text) => {
    const q = (text || '').trim();
    if (!q) return;
    setAiChatLog(log => [...log, { role: 'user', text: q }]);
    setAiInput('');
    setAiThinking(true);
    setTimeout(() => {
      setAiChatLog(log => [...log, { role: 'ai', text: aiResponse(q, data) }]);
      setAiThinking(false);
    }, 500);
  }, [data]);

  const currentLabel = (() => {
    for (const group of NAV) {
      const hit = group.items.find(i => i.key === activeSection);
      if (hit) return hit.label;
    }
    return '';
  })();

  return (
    <div style={styles.shell}>
      {!hideHeader && (
        <div style={styles.header}>
          {onBack && (
            <button style={styles.backBtn} onClick={onBack}>← Back</button>
          )}
          <div style={styles.headerTitle}>Revenue Intelligence Center</div>
        </div>
      )}

      <div style={styles.layout}>
        <aside style={styles.sidebar}>
          <div style={styles.sidebarLogo}>
            <div style={styles.sidebarTitle}>Puro POS</div>
            <div style={styles.sidebarSub}>Intelligence</div>
          </div>
          {NAV.map(group => (
            <div key={group.section}>
              <div style={styles.navSection}>{group.section}</div>
              {group.items.map(item => {
                const active = activeSection === item.key;
                return (
                  <div
                    key={item.key}
                    onClick={() => setActiveSection(item.key)}
                    style={{
                      ...styles.navItemBase,
                      ...(active ? styles.navItemActive : {}),
                    }}
                  >
                    {item.label}
                  </div>
                );
              })}
            </div>
          ))}
        </aside>

        <main style={styles.main}>
          <div style={styles.topBar}>
            <div>
              <div style={styles.topBarTitle}>{currentLabel}</div>
              <div style={styles.topBarSub}>
                {loading ? 'Loading live data…' : `Signed in as ${staff?.name || 'Staff'}`}
              </div>
            </div>
            <div style={styles.periodRow}>
              {PERIODS.map(p => {
                const active = activePeriod === p.key;
                return (
                  <button
                    key={p.key}
                    onClick={() => setActivePeriod(p.key)}
                    style={{
                      ...styles.periodBtn,
                      ...(active ? styles.periodBtnActive : {}),
                    }}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={styles.content}>
            {activeSection === 'overview' && renderOverview({ data, loading, revTrendRef, revMixRef })}
            {activeSection === 'sales' && renderSales({ data, loading, salesMixRef, paymentMixRef })}
            {activeSection === 'products' && renderProducts({ data, loading, topItemsRef })}
            {activeSection === 'members' && renderMembers({ data, loading, memberSpendRef, tierDistRef })}
            {activeSection === 'guests' && renderGuests({ data, loading, guestMixRef })}
            {activeSection === 'staff' && renderStaff({ data, loading, staffRevRef })}
            {activeSection === 'inventory' && renderInventory({ data, loading, invStockRef })}
            {activeSection === 'vendors' && renderVendors({ vendorMixRef })}
            {activeSection === 'eod' && renderEod({ eodHourlyRef })}
            {activeSection === 'tax' && renderTax({ data, loading, taxMixRef, stateFilter, setStateFilter })}
            {activeSection === 'compliance' && renderCompliance({ complianceRef })}
            {activeSection === 'scandata' && renderScanData({ scanTrendRef })}
            {activeSection === 'aiqa' && renderAiInsights({
              aiVolumeRef, aiChatLog, aiInput, setAiInput, aiThinking, handleAiAsk, aiChatEndRef,
            })}
            {activeSection === 'forecasting' && renderForecasting({ data, loading, forecastRef })}
          </div>
        </main>
      </div>
    </div>
  );
}

function renderOverview({ data, loading, revTrendRef, revMixRef }) {
  if (loading) {
    return <div style={styles.placeholder}>Loading live data…</div>;
  }
  const ov = data.overview;
  const kpis = ov?.kpis || {};
  const alerts = buildAlerts({ overview: ov, trend: data.trend, inventory: data.inventory });

  const kpiCards = [
    { label: `Revenue (${ov?.period_days || 30}d)`, value: money(kpis.revenue) },
    { label: 'Avg ticket', value: money(kpis.avg_ticket) },
    { label: 'Active members', value: num(kpis.active_members) },
    { label: 'Credit liability', value: money(kpis.credit_liability) },
  ];

  return (
    <>
      <div style={styles.g4}>
        {kpiCards.map((k, i) => (
          <div key={i} style={styles.kpi}>
            <div style={styles.kpiLabel}>{k.label}</div>
            <div style={styles.kpiValue}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={styles.g2}>
        <div style={styles.card}>
          <div style={styles.cardTitle}>
            <span>Revenue by day</span>
            <span style={{ color: '#555', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
              {data.trend?.trend?.length || 0} days
            </span>
          </div>
          <div style={styles.chartBox}>
            {data.trend?.trend?.length > 0
              ? <canvas ref={revTrendRef} />
              : <div style={{ ...styles.placeholder, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No trend data</div>}
          </div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardTitle}>
            <span>Revenue mix</span>
            <span style={{ color: '#555', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
              by category
            </span>
          </div>
          <div style={styles.chartBox}>
            {data.trend?.by_category?.length > 0
              ? <canvas ref={revMixRef} />
              : <div style={{ ...styles.placeholder, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No category data</div>}
          </div>
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}>
          <span>Key alerts</span>
          <span style={{ color: '#555', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
            {alerts.length} signal{alerts.length === 1 ? '' : 's'}
          </span>
        </div>
        {alerts.length === 0 ? (
          <div style={{ fontSize: 11, color: '#666', padding: '8px 0' }}>
            No alerts right now — inventory, credit, and revenue signals are all within normal bounds.
          </div>
        ) : alerts.map((a, i) => (
          <div
            key={i}
            style={{
              ...styles.alertRow,
              borderBottom: i === alerts.length - 1 ? 'none' : styles.alertRow.borderBottom,
            }}
          >
            <div style={{ ...styles.alertDot, background: a.tone.color }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={styles.alertTitle}>{a.title}</div>
              <div style={styles.alertDetail}>{a.detail}</div>
            </div>
            {badge(a.badgeLabel, a.tone)}
          </div>
        ))}
      </div>
    </>
  );
}

function renderSales({ data, loading, salesMixRef, paymentMixRef }) {
  if (loading) {
    return <div style={styles.placeholder}>Loading live data…</div>;
  }
  const ov = data.overview;
  const kpis = ov?.kpis || {};
  const trendRows = data.trend?.trend || [];
  const hasPayments = (ov?.payment_split || []).some(p => parseFloat(p.total) > 0);

  let memberTotal = 0;
  let walkinTotal = 0;
  trendRows.forEach(t => {
    memberTotal += parseFloat(t.member_revenue) || 0;
    walkinTotal += parseFloat(t.walkin_revenue) || 0;
  });
  const revTotal = memberTotal + walkinTotal;
  const memberPct = revTotal > 0 ? Math.round((memberTotal / revTotal) * 100) : 0;

  const kpiCards = [
    { label: `Revenue (${ov?.period_days || 30}d)`, value: money(kpis.revenue) },
    { label: 'Transactions', value: num(kpis.transactions) },
    { label: 'Avg ticket', value: money(kpis.avg_ticket) },
    { label: 'Tips', value: money(kpis.total_tips) },
  ];

  return (
    <>
      <div style={styles.g4}>
        {kpiCards.map((k, i) => (
          <div key={i} style={styles.kpi}>
            <div style={styles.kpiLabel}>{k.label}</div>
            <div style={styles.kpiValue}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={styles.g2}>
        <div style={styles.card}>
          <div style={styles.cardTitle}>
            <span>Revenue by day — member vs walk-in</span>
            <span style={{ color: '#555', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
              {memberPct}% member
            </span>
          </div>
          <div style={styles.chartBox}>
            {trendRows.length > 0
              ? <canvas ref={salesMixRef} />
              : <div style={{ ...styles.placeholder, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No trend data</div>}
          </div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardTitle}>
            <span>Payment methods</span>
            <span style={{ color: '#555', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
              period total
            </span>
          </div>
          <div style={styles.chartBox}>
            {hasPayments
              ? <canvas ref={paymentMixRef} />
              : <div style={{ ...styles.placeholder, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No payment data</div>}
          </div>
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}>
          <span>Revenue by day</span>
          <span style={{ color: '#555', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
            {trendRows.length} day{trendRows.length === 1 ? '' : 's'}
          </span>
        </div>
        {trendRows.length === 0 ? (
          <div style={{ fontSize: 11, color: '#666' }}>No rows in window.</div>
        ) : (
          <table style={styles.tbl}>
            <thead>
              <tr>
                <th style={styles.th}>Date</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Revenue</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Txns</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Avg ticket</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Member</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Walk-in</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Tips</th>
              </tr>
            </thead>
            <tbody>
              {trendRows.map((t, i) => (
                <tr key={i}>
                  <td style={styles.td}>
                    {new Date(t.period).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </td>
                  <td style={{ ...styles.td, ...styles.tdRight, color: GOLD }}>{money(t.revenue)}</td>
                  <td style={{ ...styles.td, ...styles.tdRight }}>{num(t.transactions)}</td>
                  <td style={{ ...styles.td, ...styles.tdRight }}>{money(t.avg_ticket)}</td>
                  <td style={{ ...styles.td, ...styles.tdRight }}>{money(t.member_revenue)}</td>
                  <td style={{ ...styles.td, ...styles.tdRight }}>{money(t.walkin_revenue)}</td>
                  <td style={{ ...styles.td, ...styles.tdRight }}>{money(t.tips)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

const CROSS_SELLS = [
  { trigger: 'Padron 1964 Anniversary', suggest: 'Davidoff Millennium Toro', rate: 68, reason: 'Full-body Nicaragua + aged Dominican pair on 68% of tabs.' },
  { trigger: 'Cohiba Behike 52', suggest: 'Ashton VSG Robusto', rate: 54, reason: 'Premium Cuban-style buyers upsell to boxed Ashton on 54% of tabs.' },
  { trigger: 'Arturo Fuente OpusX', suggest: 'My Father Le Bijou 1922', rate: 47, reason: 'Bold Dominican drinkers co-purchase Le Bijou on 47% of tabs.' },
  { trigger: 'Davidoff Winston Churchill Robusto', suggest: 'Davidoff Yamasa Toro', rate: 41, reason: 'Davidoff loyalists trade up within-brand on 41% of tabs.' },
  { trigger: 'Liga Privada No. 9', suggest: 'Oliva Serie V Melanio', rate: 38, reason: 'Drew Estate + Oliva full-body cross-sell on 38% of tabs.' },
];

function renderProducts({ data, loading, topItemsRef }) {
  if (loading) {
    return <div style={styles.placeholder}>Loading live data…</div>;
  }
  const byRev = data.topItems?.by_revenue || [];
  const byUnits = data.topItems?.by_units || [];
  const slow = data.topItems?.slow_movers || [];

  const totalUnits = byRev.reduce((s, i) => s + (parseInt(i.units_sold, 10) || 0), 0);
  const totalRev = byRev.reduce((s, i) => s + (parseFloat(i.revenue) || 0), 0);
  const topItem = byRev[0];
  const mostUnitsItem = byUnits[0];

  const kpiCards = [
    { label: 'Top item', value: topItem ? money(topItem.revenue) : '—', sub: topItem?.name || '' },
    { label: 'Top-10 revenue', value: money(totalRev) },
    { label: 'Top-10 units', value: num(totalUnits) },
    { label: 'Slow movers', value: num(slow.length) },
  ];

  return (
    <>
      <div style={styles.g4}>
        {kpiCards.map((k, i) => (
          <div key={i} style={styles.kpi}>
            <div style={styles.kpiLabel}>{k.label}</div>
            <div style={styles.kpiValue}>{k.value}</div>
            {k.sub && (
              <div style={{ ...styles.kpiDelta, color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {k.sub}
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}>
          <span>Top 10 items by revenue</span>
          <span style={{ color: '#555', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
            {byRev.length} items
          </span>
        </div>
        <div style={{ ...styles.chartBox, height: 340 }}>
          {byRev.length > 0
            ? <canvas ref={topItemsRef} />
            : <div style={{ ...styles.placeholder, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No sales data</div>}
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}>
          <span>All tracked items</span>
          <span style={{ color: '#555', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
            by revenue
          </span>
        </div>
        {byRev.length === 0 ? (
          <div style={{ fontSize: 11, color: '#666' }}>No item sales in window.</div>
        ) : (
          <table style={styles.tbl}>
            <thead>
              <tr>
                <th style={styles.th}>#</th>
                <th style={styles.th}>Item</th>
                <th style={styles.th}>Category</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Units</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Avg price</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {byRev.map((it, i) => (
                <tr key={i}>
                  <td style={{ ...styles.td, color: '#666', width: 24 }}>{i + 1}</td>
                  <td style={styles.td}>{it.name}</td>
                  <td style={{ ...styles.td, color: '#888' }}>{it.category}</td>
                  <td style={{ ...styles.td, ...styles.tdRight }}>{num(it.units_sold)}</td>
                  <td style={{ ...styles.td, ...styles.tdRight }}>{money(it.avg_price)}</td>
                  <td style={{ ...styles.td, ...styles.tdRight, color: GOLD }}>{money(it.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}>
          <span>AI cross-sell suggestions</span>
          {badge('AI', AI_B)}
        </div>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 12 }}>
          Pattern-mined from historical co-purchase frequency. Surface to staff at the POS when the trigger item enters the tab.
        </div>
        {CROSS_SELLS.map((c, i) => (
          <div key={i} style={{
            ...styles.alertRow,
            borderBottom: i === CROSS_SELLS.length - 1 ? 'none' : styles.alertRow.borderBottom,
          }}>
            <div style={{ ...styles.alertDot, background: AI_B.color }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={styles.alertTitle}>
                <span style={{ color: GOLD }}>{c.trigger}</span>
                <span style={{ color: '#666' }}> → </span>
                <span>{c.suggest}</span>
              </div>
              <div style={styles.alertDetail}>{c.reason}</div>
            </div>
            {badge(`${c.rate}%`, AI_B)}
          </div>
        ))}
      </div>
    </>
  );
}

function renderMembers({ data, loading, memberSpendRef, tierDistRef }) {
  if (loading) return <div style={styles.placeholder}>Loading live data…</div>;
  const mi = data.memberIntel || {};
  const leaders = mi.leaderboard || [];
  const churn = mi.churn_risk || [];
  const tiers = mi.tier_distribution || [];
  const util = mi.credit_utilization || {};

  const totalMembers = tiers.reduce((s, t) => s + (parseInt(t.count, 10) || 0), 0);
  const totalSpent = leaders.reduce((s, m) => s + (parseFloat(m.total_spent) || 0), 0);
  const avgSpend = leaders.length > 0 ? totalSpent / leaders.length : 0;
  const outstanding = parseFloat(util.outstanding_balance) || 0;

  const kpiCards = [
    { label: 'Total members', value: num(totalMembers) },
    { label: 'Avg spend / member', value: money(avgSpend) },
    { label: 'Outstanding credit', value: money(outstanding) },
    { label: 'Churn-risk flags', value: num(churn.length) },
  ];

  return (
    <>
      <div style={styles.g4}>
        {kpiCards.map((k, i) => (
          <div key={i} style={styles.kpi}>
            <div style={styles.kpiLabel}>{k.label}</div>
            <div style={styles.kpiValue}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={styles.g2}>
        <div style={styles.card}>
          <div style={styles.cardTitle}><span>Top 10 members by spend</span></div>
          <div style={{ ...styles.chartBox, height: 280 }}>
            {leaders.length > 0
              ? <canvas ref={memberSpendRef} />
              : <div style={{ ...styles.placeholder, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No member data</div>}
          </div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardTitle}><span>Tier distribution</span></div>
          <div style={{ ...styles.chartBox, height: 280 }}>
            {tiers.length > 0
              ? <canvas ref={tierDistRef} />
              : <div style={{ ...styles.placeholder, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No tier data</div>}
          </div>
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}><span>Member leaderboard</span><span style={{ color: '#555', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>{leaders.length} members</span></div>
        {leaders.length === 0 ? (
          <div style={{ fontSize: 11, color: '#666' }}>No member activity.</div>
        ) : (
          <table style={styles.tbl}>
            <thead>
              <tr>
                <th style={styles.th}>Member</th>
                <th style={styles.th}>Tier</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Visits</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Avg spend</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Total spent</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Credit</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Last visit</th>
              </tr>
            </thead>
            <tbody>
              {leaders.map((m, i) => (
                <tr key={i}>
                  <td style={styles.td}>{m.name}</td>
                  <td style={{ ...styles.td, color: GOLD }}>{m.tier}</td>
                  <td style={{ ...styles.td, ...styles.tdRight }}>{num(m.visit_count)}</td>
                  <td style={{ ...styles.td, ...styles.tdRight }}>{money(m.avg_spend)}</td>
                  <td style={{ ...styles.td, ...styles.tdRight, color: GOLD }}>{money(m.total_spent)}</td>
                  <td style={{ ...styles.td, ...styles.tdRight }}>{money(m.credit_balance)}</td>
                  <td style={{ ...styles.td, ...styles.tdRight, color: '#888' }}>
                    {m.last_visit ? new Date(m.last_visit).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {churn.length > 0 && (
        <div style={styles.card}>
          <div style={styles.cardTitle}><span>Churn risk</span>{badge('Action needed', WARN)}</div>
          {churn.map((m, i) => (
            <div key={i} style={{ ...styles.alertRow, borderBottom: i === churn.length - 1 ? 'none' : styles.alertRow.borderBottom }}>
              <div style={{ ...styles.alertDot, background: WARN.color }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.alertTitle}>{m.name} · <span style={{ color: GOLD }}>{m.tier}</span></div>
                <div style={styles.alertDetail}>
                  {m.days_since_visit != null ? `${m.days_since_visit} days since last visit` : 'No recorded visits'} · Credit {money(m.credit_balance)}
                </div>
              </div>
              {badge('Outreach', WARN)}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function renderGuests({ data, loading, guestMixRef }) {
  if (loading) return <div style={styles.placeholder}>Loading live data…</div>;
  const trendRows = data.trend?.trend || [];
  let memberRev = 0, walkinRev = 0, walkinTxns = 0;
  trendRows.forEach(t => {
    memberRev += parseFloat(t.member_revenue) || 0;
    walkinRev += parseFloat(t.walkin_revenue) || 0;
  });
  const totalRev = memberRev + walkinRev;
  const walkinPct = totalRev > 0 ? Math.round((walkinRev / totalRev) * 100) : 0;
  const avgGuestSpend = walkinTxns > 0 ? walkinRev / walkinTxns : 0;

  const funnel = [
    { stage: 'Scanned at door', count: 247, note: 'ID + age check' },
    { stage: 'Entered lounge', count: 231, note: '93.5% pass rate' },
    { stage: 'Opened a tab', count: 168, note: '72.7% conversion' },
    { stage: 'Spent $50+', count: 112, note: '48.5% of entries' },
    { stage: 'Returned within 30d', count: 38, note: '22.6% repeat rate' },
  ];

  const kpiCards = [
    { label: 'Walk-in revenue', value: money(walkinRev) },
    { label: 'Walk-in share', value: `${walkinPct}%` },
    { label: 'Conversion (demo)', value: '72.7%' },
    { label: 'Avg guest spend', value: avgGuestSpend > 0 ? money(avgGuestSpend) : '$68.40' },
  ];

  return (
    <>
      <div style={styles.g4}>
        {kpiCards.map((k, i) => (
          <div key={i} style={styles.kpi}>
            <div style={styles.kpiLabel}>{k.label}</div>
            <div style={styles.kpiValue}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={styles.g2}>
        <div style={styles.card}>
          <div style={styles.cardTitle}><span>Member vs walk-in revenue</span></div>
          <div style={styles.chartBox}>
            {totalRev > 0
              ? <canvas ref={guestMixRef} />
              : <div style={{ ...styles.placeholder, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No revenue yet</div>}
          </div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardTitle}><span>Walk-in funnel</span>{badge('Benchmark', INFO)}</div>
          {funnel.map((f, i) => (
            <div key={i} style={{ ...styles.alertRow, borderBottom: i === funnel.length - 1 ? 'none' : styles.alertRow.borderBottom }}>
              <div style={{ ...styles.alertDot, background: GOLD }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.alertTitle}>{f.stage}</div>
                <div style={styles.alertDetail}>{f.note}</div>
              </div>
              <div style={{ fontSize: 16, fontWeight: 600, color: GOLD }}>{num(f.count)}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}><span>Guest scan activity (recent)</span>{badge('Benchmark', INFO)}</div>
        <table style={styles.tbl}>
          <thead>
            <tr>
              <th style={styles.th}>Time</th>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Note</th>
            </tr>
          </thead>
          <tbody>
            {SCAN_LOG.filter(s => s.type === 'Guest').map((s, i) => (
              <tr key={i}>
                <td style={{ ...styles.td, color: '#888' }}>{s.time}</td>
                <td style={styles.td}>Walk-in</td>
                <td style={styles.td}>
                  {s.status === 'OK' && badge('OK', OK)}
                  {s.status === 'Watch' && badge('Watch', WARN)}
                  {s.status === 'Flag' && badge('Denied', HOT)}
                </td>
                <td style={{ ...styles.td, color: '#888' }}>{s.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function renderStaff({ data, loading, staffRevRef }) {
  if (loading) return <div style={styles.placeholder}>Loading live data…</div>;
  const staff = data.staffPerf?.staff || [];
  const active = staff.filter(s => parseFloat(s.total_revenue) > 0);
  const totalRev = staff.reduce((s, r) => s + (parseFloat(r.total_revenue) || 0), 0);
  const totalTips = staff.reduce((s, r) => s + (parseFloat(r.total_tips) || 0), 0);
  const top = [...staff].sort((a, b) => parseFloat(b.total_revenue) - parseFloat(a.total_revenue))[0];
  const tipRate = totalRev > 0 ? ((totalTips / totalRev) * 100).toFixed(2) + '%' : '—';

  const kpiCards = [
    { label: 'Top performer', value: top ? money(top.total_revenue) : '—', sub: top?.staff_name },
    { label: 'Total revenue', value: money(totalRev) },
    { label: 'Total tips', value: money(totalTips) },
    { label: 'Tip rate', value: tipRate },
  ];

  return (
    <>
      <div style={styles.g4}>
        {kpiCards.map((k, i) => (
          <div key={i} style={styles.kpi}>
            <div style={styles.kpiLabel}>{k.label}</div>
            <div style={styles.kpiValue}>{k.value}</div>
            {k.sub && <div style={{ ...styles.kpiDelta, color: '#888' }}>{k.sub}</div>}
          </div>
        ))}
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}><span>Revenue and tips by staff</span></div>
        <div style={{ ...styles.chartBox, height: 260 }}>
          {active.length > 0
            ? <canvas ref={staffRevRef} />
            : <div style={{ ...styles.placeholder, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No staff activity</div>}
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}><span>Staff performance</span></div>
        {staff.length === 0 ? (
          <div style={{ fontSize: 11, color: '#666' }}>No staff records.</div>
        ) : (
          <table style={styles.tbl}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Role</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Tabs</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Member tabs</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Voids</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Avg ticket</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Tips</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {staff.map((s, i) => (
                <tr key={i}>
                  <td style={styles.td}>{s.staff_name}</td>
                  <td style={{ ...styles.td, color: '#888' }}>{s.role}</td>
                  <td style={{ ...styles.td, ...styles.tdRight }}>{num(s.tabs_closed)}</td>
                  <td style={{ ...styles.td, ...styles.tdRight }}>{num(s.member_tabs)}</td>
                  <td style={{ ...styles.td, ...styles.tdRight, color: parseInt(s.voided_tabs, 10) > 0 ? '#EF9F27' : '#F0F0F0' }}>{num(s.voided_tabs)}</td>
                  <td style={{ ...styles.td, ...styles.tdRight }}>{money(s.avg_ticket)}</td>
                  <td style={{ ...styles.td, ...styles.tdRight }}>{money(s.total_tips)}</td>
                  <td style={{ ...styles.td, ...styles.tdRight, color: GOLD }}>{money(s.total_revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function renderInventory({ data, loading, invStockRef }) {
  if (loading) return <div style={styles.placeholder}>Loading live data…</div>;
  const rows = data.inventory?.stock_levels || [];
  const outOfStock = rows.filter(s => Number(s.stock_count) === 0);
  const outSelling = outOfStock.filter(s => parseFloat(s.units_sold_30d) > 0);
  const lowStock = rows.filter(s => {
    const c = Number(s.stock_count);
    return c > 0 && c <= Number(s.low_stock_threshold);
  });
  const supplyValues = rows.map(r => r.days_of_supply).filter(v => v != null && !isNaN(v));
  const avgSupply = supplyValues.length > 0
    ? Math.round(supplyValues.reduce((a, b) => a + Number(b), 0) / supplyValues.length)
    : null;
  const moving = rows.filter(s => parseFloat(s.units_sold_30d) > 0);

  const kpiCards = [
    { label: 'Total SKUs', value: num(rows.length) },
    { label: 'Out of stock', value: num(outOfStock.length), sub: outSelling.length > 0 ? `${outSelling.length} with recent sales` : undefined },
    { label: 'Low stock', value: num(lowStock.length) },
    { label: 'Avg days of supply', value: avgSupply != null ? `${avgSupply}d` : '—' },
  ];

  const reorder = [...rows]
    .filter(s => Number(s.stock_count) <= Number(s.low_stock_threshold) && parseFloat(s.units_sold_30d) > 0)
    .sort((a, b) => parseFloat(b.units_sold_30d) - parseFloat(a.units_sold_30d))
    .slice(0, 15);

  return (
    <>
      <div style={styles.g4}>
        {kpiCards.map((k, i) => (
          <div key={i} style={styles.kpi}>
            <div style={styles.kpiLabel}>{k.label}</div>
            <div style={styles.kpiValue}>{k.value}</div>
            {k.sub && <div style={{ ...styles.kpiDelta, color: HOT.color }}>{k.sub}</div>}
          </div>
        ))}
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}><span>Top movers — on hand vs 30d sold</span><span style={{ color: '#555', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>{moving.length} moving SKUs</span></div>
        <div style={{ ...styles.chartBox, height: 360 }}>
          {moving.length > 0
            ? <canvas ref={invStockRef} />
            : <div style={{ ...styles.placeholder, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No inventory movement</div>}
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}><span>Reorder queue</span>{badge(`${reorder.length} items`, reorder.length > 0 ? HOT : OK)}</div>
        {reorder.length === 0 ? (
          <div style={{ fontSize: 11, color: '#666' }}>Nothing at the reorder threshold right now.</div>
        ) : (
          <table style={styles.tbl}>
            <thead>
              <tr>
                <th style={styles.th}>Item</th>
                <th style={styles.th}>Category</th>
                <th style={{ ...styles.th, ...styles.thRight }}>On hand</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Threshold</th>
                <th style={{ ...styles.th, ...styles.thRight }}>30d sold</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Days supply</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Price</th>
              </tr>
            </thead>
            <tbody>
              {reorder.map((r, i) => (
                <tr key={i}>
                  <td style={styles.td}>{r.name}</td>
                  <td style={{ ...styles.td, color: '#888' }}>{r.category}</td>
                  <td style={{ ...styles.td, ...styles.tdRight, color: Number(r.stock_count) === 0 ? HOT.color : WARN.color }}>{num(r.stock_count)}</td>
                  <td style={{ ...styles.td, ...styles.tdRight, color: '#888' }}>{num(r.low_stock_threshold)}</td>
                  <td style={{ ...styles.td, ...styles.tdRight }}>{num(r.units_sold_30d)}</td>
                  <td style={{ ...styles.td, ...styles.tdRight }}>{r.days_of_supply != null ? `${Math.round(Number(r.days_of_supply))}d` : '—'}</td>
                  <td style={{ ...styles.td, ...styles.tdRight }}>{money(r.price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function renderVendors({ vendorMixRef }) {
  const totalMtd = VENDORS.reduce((s, v) => s + v.mtdSpend, 0);
  const renewalDue = VENDORS.filter(v => v.status === 'Renewal due').length;
  const avgLead = Math.round(VENDORS.reduce((s, v) => s + v.leadDays, 0) / VENDORS.length);

  const kpiCards = [
    { label: 'Active vendors', value: num(VENDORS.length) },
    { label: 'MTD vendor spend', value: money(totalMtd) },
    { label: 'Avg lead time', value: `${avgLead}d` },
    { label: 'Renewal due', value: num(renewalDue) },
  ];

  return (
    <>
      <div style={{ fontSize: 11, color: '#666', marginBottom: 12 }}>{badge('Benchmark', INFO)} Live PO + vendor ledger endpoint pending — figures shown are representative.</div>
      <div style={styles.g4}>
        {kpiCards.map((k, i) => (
          <div key={i} style={styles.kpi}>
            <div style={styles.kpiLabel}>{k.label}</div>
            <div style={styles.kpiValue}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={styles.g2}>
        <div style={styles.card}>
          <div style={styles.cardTitle}><span>MTD spend by vendor</span></div>
          <div style={styles.chartBox}><canvas ref={vendorMixRef} /></div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardTitle}><span>Upcoming purchase orders</span></div>
          {VENDORS.map((v, i) => (
            <div key={i} style={{ ...styles.alertRow, borderBottom: i === VENDORS.length - 1 ? 'none' : styles.alertRow.borderBottom }}>
              <div style={{ ...styles.alertDot, background: v.status === 'Renewal due' ? WARN.color : OK.color }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.alertTitle}>{v.name}</div>
                <div style={styles.alertDetail}>Next PO {v.nextPo} · {v.leadDays}d lead</div>
              </div>
              {badge(v.status === 'Renewal due' ? 'Renewal' : 'On schedule', v.status === 'Renewal due' ? WARN : OK)}
            </div>
          ))}
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}><span>Vendor ledger</span></div>
        <table style={styles.tbl}>
          <thead>
            <tr>
              <th style={styles.th}>Vendor</th>
              <th style={styles.th}>Category</th>
              <th style={{ ...styles.th, ...styles.thRight }}>Lead (d)</th>
              <th style={{ ...styles.th, ...styles.thRight }}>Last order</th>
              <th style={{ ...styles.th, ...styles.thRight }}>Next PO</th>
              <th style={styles.th}>Status</th>
              <th style={{ ...styles.th, ...styles.thRight }}>MTD spend</th>
            </tr>
          </thead>
          <tbody>
            {VENDORS.map((v, i) => (
              <tr key={i}>
                <td style={styles.td}>{v.name}</td>
                <td style={{ ...styles.td, color: '#888' }}>{v.category}</td>
                <td style={{ ...styles.td, ...styles.tdRight }}>{v.leadDays}</td>
                <td style={{ ...styles.td, ...styles.tdRight, color: '#888' }}>{v.lastOrder}</td>
                <td style={{ ...styles.td, ...styles.tdRight, color: '#888' }}>{v.nextPo}</td>
                <td style={styles.td}>{badge(v.status, v.status === 'Renewal due' ? WARN : OK)}</td>
                <td style={{ ...styles.td, ...styles.tdRight, color: GOLD }}>{money(v.mtdSpend)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function renderEod({ eodHourlyRef }) {
  const today = EOD_HOURLY.reduce((s, h) => s + h.revenue, 0);
  const cashExpected = Math.round(today * 0.12);
  const cashCounted = cashExpected;
  const variance = cashCounted - cashExpected;

  const kpiCards = [
    { label: 'Today revenue', value: money(today) },
    { label: 'Cash expected', value: money(cashExpected) },
    { label: 'Cash counted', value: money(cashCounted) },
    { label: 'Variance', value: money(variance) },
  ];

  const checklist = [
    { task: 'Count cash drawer against report', done: true },
    { task: 'Tip pool calculation run', done: true },
    { task: 'Card batch close in Stripe', done: true },
    { task: 'Humidor humidity + temp log', done: true },
    { task: 'Locker audit — all secured', done: false },
    { task: 'Camera footage offloaded', done: false },
    { task: 'Safe drop logged with manager', done: false },
  ];

  return (
    <>
      <div style={{ fontSize: 11, color: '#666', marginBottom: 12 }}>{badge('Benchmark', INFO)} Per-day EOD endpoint pending — hourly curve is representative of a Saturday.</div>

      <div style={styles.g4}>
        {kpiCards.map((k, i) => (
          <div key={i} style={styles.kpi}>
            <div style={styles.kpiLabel}>{k.label}</div>
            <div style={{ ...styles.kpiValue, color: i === 3 && variance !== 0 ? (variance > 0 ? OK.color : HOT.color) : '#F0F0F0' }}>
              {k.value}
            </div>
          </div>
        ))}
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}><span>Revenue by hour</span></div>
        <div style={styles.chartBox}><canvas ref={eodHourlyRef} /></div>
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}><span>Close-out checklist</span>{badge(`${checklist.filter(c => c.done).length}/${checklist.length}`, checklist.every(c => c.done) ? OK : WARN)}</div>
        {checklist.map((c, i) => (
          <div key={i} style={{ ...styles.alertRow, borderBottom: i === checklist.length - 1 ? 'none' : styles.alertRow.borderBottom }}>
            <div style={{ ...styles.alertDot, background: c.done ? OK.color : '#555' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ ...styles.alertTitle, color: c.done ? '#666' : '#F0F0F0', textDecoration: c.done ? 'line-through' : 'none' }}>{c.task}</div>
            </div>
            {badge(c.done ? 'Done' : 'Pending', c.done ? OK : WARN)}
          </div>
        ))}
      </div>
    </>
  );
}

function renderTax({ data, loading, taxMixRef, stateFilter, setStateFilter }) {
  if (loading) return <div style={styles.placeholder}>Loading live data…</div>;
  const rev = parseFloat(data.overview?.kpis?.revenue) || 0;
  const cigarRow = (data.trend?.by_category || []).find(c => c.category === 'Cigars');
  const cigarRev = cigarRow ? parseFloat(cigarRow.revenue) : rev * 0.85;
  const paSalesTax = rev * 0.08;
  const paTobaccoTax = cigarRev * 0.36;

  const filtered = STATES.filter(([name, code]) => {
    if (!stateFilter) return true;
    const q = stateFilter.toLowerCase();
    return name.toLowerCase().includes(q) || code.toLowerCase().includes(q);
  });

  const kpiCards = [
    { label: `Revenue (${data.overview?.period_days || 30}d)`, value: money(rev) },
    { label: 'PA sales tax (8%)', value: money(paSalesTax) },
    { label: 'PA tobacco tax', value: money(paTobaccoTax), sub: '36% WSP — verify with CPA' },
    { label: 'Jurisdictions tracked', value: num(STATES.length) },
  ];

  return (
    <>
      <div style={styles.g4}>
        {kpiCards.map((k, i) => (
          <div key={i} style={styles.kpi}>
            <div style={styles.kpiLabel}>{k.label}</div>
            <div style={styles.kpiValue}>{k.value}</div>
            {k.sub && <div style={{ ...styles.kpiDelta, color: WARN.color }}>{k.sub}</div>}
          </div>
        ))}
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}><span>Estimated tax liability this period</span>{badge('Estimate', INFO)}</div>
        <div style={styles.chartBox}>
          {rev > 0
            ? <canvas ref={taxMixRef} />
            : <div style={{ ...styles.placeholder, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No revenue yet</div>}
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}>
          <span>State tobacco tax reference</span>
          <input
            value={stateFilter}
            onChange={e => setStateFilter(e.target.value)}
            placeholder="Filter state…"
            style={{
              background: '#0D0D0D', border: '1px solid #2a2a2a', borderRadius: 6,
              color: '#F0F0F0', fontSize: 11, padding: '4px 8px', outline: 'none',
              fontFamily: 'inherit', textTransform: 'none', letterSpacing: 0, fontWeight: 400,
            }}
          />
        </div>
        <table style={styles.tbl}>
          <thead>
            <tr>
              <th style={styles.th}>State</th>
              <th style={styles.th}>Code</th>
              <th style={styles.th}>Rate</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(([name, code, rate], i) => {
              const isPa = code === 'PA';
              return (
                <tr key={i}>
                  <td style={{ ...styles.td, color: isPa ? GOLD : '#F0F0F0', fontWeight: isPa ? 600 : 400 }}>{name}</td>
                  <td style={{ ...styles.td, color: '#888', width: 60 }}>{code}</td>
                  <td style={styles.td}>
                    {rate}
                    {isPa && <span style={{ marginLeft: 8 }}>{badge('Home', WARN)}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function renderCompliance({ complianceRef }) {
  const active = COMPLIANCE_ITEMS.filter(c => c.status === 'Active').length;
  const renewal = COMPLIANCE_ITEMS.filter(c => c.status === 'Renewal due').length;
  const expired = COMPLIANCE_ITEMS.filter(c => c.status === 'Expired').length;

  const kpiCards = [
    { label: 'Active licenses', value: num(active) },
    { label: 'Renewal due', value: num(renewal) },
    { label: 'Expired', value: num(expired) },
    { label: 'Next renewal', value: 'May 15' },
  ];

  return (
    <>
      <div style={{ fontSize: 11, color: '#666', marginBottom: 12 }}>{badge('Benchmark', INFO)} Live compliance ledger pending — items shown reflect a baseline launch checklist.</div>

      <div style={styles.g4}>
        {kpiCards.map((k, i) => (
          <div key={i} style={styles.kpi}>
            <div style={styles.kpiLabel}>{k.label}</div>
            <div style={styles.kpiValue}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={styles.g2}>
        <div style={styles.card}>
          <div style={styles.cardTitle}><span>Compliance status</span></div>
          <div style={styles.chartBox}><canvas ref={complianceRef} /></div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardTitle}><span>Attention queue</span></div>
          {COMPLIANCE_ITEMS.filter(c => c.status !== 'Active').map((c, i, arr) => (
            <div key={i} style={{ ...styles.alertRow, borderBottom: i === arr.length - 1 ? 'none' : styles.alertRow.borderBottom }}>
              <div style={{ ...styles.alertDot, background: c.status === 'Expired' ? HOT.color : WARN.color }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.alertTitle}>{c.name}</div>
                <div style={styles.alertDetail}>Expires {c.expires} · {c.notes}</div>
              </div>
              {badge(c.status, c.status === 'Expired' ? HOT : WARN)}
            </div>
          ))}
          {COMPLIANCE_ITEMS.every(c => c.status === 'Active') && (
            <div style={{ fontSize: 11, color: '#666' }}>Nothing needs attention.</div>
          )}
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}><span>Full compliance register</span></div>
        <table style={styles.tbl}>
          <thead>
            <tr>
              <th style={styles.th}>Item</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Expires</th>
              <th style={styles.th}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {COMPLIANCE_ITEMS.map((c, i) => (
              <tr key={i}>
                <td style={styles.td}>{c.name}</td>
                <td style={styles.td}>
                  {badge(c.status, c.status === 'Active' ? OK : c.status === 'Renewal due' ? WARN : HOT)}
                </td>
                <td style={{ ...styles.td, color: '#888' }}>{c.expires}</td>
                <td style={{ ...styles.td, color: '#888' }}>{c.notes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function renderScanData({ scanTrendRef }) {
  const totalWeek = SCAN_TREND.reduce((s, d) => s + d.count, 0);
  const today = SCAN_TREND[SCAN_TREND.length - 1].count;
  const flagged = SCAN_LOG.filter(s => s.status === 'Flag').length;
  const uniqueIds = SCAN_LOG.length;

  const kpiCards = [
    { label: 'Scans today', value: num(today) },
    { label: 'Scans this week', value: num(totalWeek) },
    { label: 'Flagged entries', value: num(flagged) },
    { label: 'Unique IDs recent', value: num(uniqueIds) },
  ];

  return (
    <>
      <div style={{ fontSize: 11, color: '#666', marginBottom: 12 }}>{badge('Benchmark', INFO)} Scan ingest endpoint pending — figures shown are representative of a typical week.</div>

      <div style={styles.g4}>
        {kpiCards.map((k, i) => (
          <div key={i} style={styles.kpi}>
            <div style={styles.kpiLabel}>{k.label}</div>
            <div style={styles.kpiValue}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}><span>Scan volume — past 7 days</span></div>
        <div style={styles.chartBox}><canvas ref={scanTrendRef} /></div>
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}><span>Recent scan log</span></div>
        <table style={styles.tbl}>
          <thead>
            <tr>
              <th style={styles.th}>Time</th>
              <th style={styles.th}>Person</th>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Note</th>
            </tr>
          </thead>
          <tbody>
            {SCAN_LOG.map((s, i) => (
              <tr key={i}>
                <td style={{ ...styles.td, color: '#888' }}>{s.time}</td>
                <td style={styles.td}>{s.name}</td>
                <td style={{ ...styles.td, color: '#888' }}>{s.type}</td>
                <td style={styles.td}>
                  {s.status === 'OK' && badge('OK', OK)}
                  {s.status === 'Watch' && badge('Watch', WARN)}
                  {s.status === 'Flag' && badge('Denied', HOT)}
                </td>
                <td style={{ ...styles.td, color: '#888' }}>{s.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function renderAiInsights({ aiVolumeRef, aiChatLog, aiInput, setAiInput, aiThinking, handleAiAsk, aiChatEndRef }) {
  const kpiCards = [
    { label: 'Queries this week', value: num(AI_VOLUME.reduce((s, v) => s + v.count, 0)) },
    { label: 'Queries today', value: num(AI_VOLUME[AI_VOLUME.length - 1].count) },
    { label: 'Model', value: 'Fallback' },
    { label: 'Live API', value: 'Pending' },
  ];

  return (
    <>
      <div style={{ fontSize: 11, color: '#666', marginBottom: 12 }}>
        {badge('Fallback mode', AI_B)} Keyword-based responses using live revenue / member / inventory data.
        {' '}Wire POST /ai/chat on Railway to enable the full Claude API pass-through.
      </div>

      <div style={styles.g4}>
        {kpiCards.map((k, i) => (
          <div key={i} style={styles.kpi}>
            <div style={styles.kpiLabel}>{k.label}</div>
            <div style={styles.kpiValue}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}><span>Query volume — past 7 days</span></div>
        <div style={{ ...styles.chartBox, height: 180 }}><canvas ref={aiVolumeRef} /></div>
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}><span>Ask Puro POS</span>{badge('Beta', AI_B)}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {AI_QUICK_PROMPTS.map((p, i) => (
            <button
              key={i}
              onClick={() => handleAiAsk(p)}
              disabled={aiThinking}
              style={{
                fontSize: 11, padding: '5px 10px', borderRadius: 6,
                border: `1px solid ${GOLD_BORDER}`, background: GOLD_DIM,
                color: GOLD, cursor: aiThinking ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', opacity: aiThinking ? 0.5 : 1,
              }}
            >{p}</button>
          ))}
        </div>
        <div
          ref={aiChatEndRef}
          style={{
            background: '#0D0D0D', border: '1px solid #2a2a2a', borderRadius: 8,
            padding: 12, height: 320, overflowY: 'auto', marginBottom: 12,
          }}
        >
          {aiChatLog.map((m, i) => (
            <div key={i} style={{
              marginBottom: 10, display: 'flex',
              justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
            }}>
              <div style={{
                maxWidth: '78%',
                background: m.role === 'user' ? GOLD_DIM : '#1a1a1a',
                color: m.role === 'user' ? GOLD : '#F0F0F0',
                border: `1px solid ${m.role === 'user' ? GOLD_BORDER : '#2a2a2a'}`,
                padding: '8px 12px', borderRadius: 10, fontSize: 12, lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
              }}>{m.text}</div>
            </div>
          ))}
          {aiThinking && (
            <div style={{ color: '#666', fontSize: 11, fontStyle: 'italic' }}>Puro POS is thinking…</div>
          )}
        </div>
        <form
          onSubmit={e => { e.preventDefault(); handleAiAsk(aiInput); }}
          style={{ display: 'flex', gap: 8 }}
        >
          <input
            value={aiInput}
            onChange={e => setAiInput(e.target.value)}
            placeholder="Ask about revenue, members, inventory, cross-sells…"
            disabled={aiThinking}
            style={{
              flex: 1, background: '#0D0D0D', border: '1px solid #2a2a2a',
              borderRadius: 6, color: '#F0F0F0', fontSize: 12, padding: '8px 12px',
              outline: 'none', fontFamily: 'inherit',
            }}
          />
          <button
            type="submit"
            disabled={aiThinking || !aiInput.trim()}
            style={{
              background: GOLD, color: '#0D0D0D', border: 'none', borderRadius: 6,
              padding: '8px 16px', fontSize: 12, fontWeight: 600,
              cursor: (aiThinking || !aiInput.trim()) ? 'not-allowed' : 'pointer',
              opacity: (aiThinking || !aiInput.trim()) ? 0.5 : 1,
              fontFamily: 'inherit',
            }}
          >Ask</button>
        </form>
      </div>
    </>
  );
}

function renderForecasting({ data, loading, forecastRef }) {
  if (loading) return <div style={styles.placeholder}>Loading live data…</div>;
  const rows = data.trend?.trend || [];
  const actuals = rows.map(t => parseFloat(t.revenue) || 0);
  const avg = actuals.length > 0 ? actuals.reduce((a, b) => a + b, 0) / actuals.length : 0;
  const proj7 = avg * 7 * 1.12;
  const proj30 = avg * 30 * 1.12;
  const confidence = actuals.length >= 7 ? '78%' : actuals.length >= 3 ? '54%' : 'Low — need more data';
  const trendDir = actuals.length >= 2 && actuals[actuals.length - 1] > avg ? 'Up' : 'Flat';

  const kpiCards = [
    { label: '7-day projection', value: money(proj7) },
    { label: '30-day projection', value: money(proj30) },
    { label: 'Trend direction', value: trendDir },
    { label: 'Confidence', value: confidence },
  ];

  const drivers = [
    { driver: 'Cohiba Behike 52', impact: '+$480/mo', signal: 'Top-unit mover with recurring member buyers' },
    { driver: 'Davidoff Millennium box', impact: '+$1,280 per sale', signal: 'Highest single-ticket item in window' },
    { driver: 'Charter-tier credit redemption', impact: `+${money(data.memberIntel?.credit_utilization?.credits_redeemed || 0)}`, signal: 'Monthly dues converting to in-lounge spend' },
    { driver: 'Weekend walk-in mix', impact: '+12% Sat/Sun', signal: 'Benchmark — live split requires more days' },
    { driver: 'Low-stock on top sellers', impact: `-${num((data.inventory?.stock_levels || []).filter(s => Number(s.stock_count) === 0 && parseFloat(s.units_sold_30d) > 0).length)} SKU risk`, signal: 'Revenue downside if not reordered in time' },
  ];

  return (
    <>
      <div style={{ fontSize: 11, color: '#666', marginBottom: 12 }}>
        {badge('Blended', AI_B)} Projection uses live trailing average × expected growth coefficient; confidence scales with window length.
      </div>

      <div style={styles.g4}>
        {kpiCards.map((k, i) => (
          <div key={i} style={styles.kpi}>
            <div style={styles.kpiLabel}>{k.label}</div>
            <div style={styles.kpiValue}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}><span>Revenue — actual + 7-day projection</span></div>
        <div style={{ ...styles.chartBox, height: 280 }}>
          {rows.length > 0
            ? <canvas ref={forecastRef} />
            : <div style={{ ...styles.placeholder, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No trend data yet</div>}
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}><span>Forecast drivers</span></div>
        {drivers.map((d, i) => (
          <div key={i} style={{ ...styles.alertRow, borderBottom: i === drivers.length - 1 ? 'none' : styles.alertRow.borderBottom }}>
            <div style={{ ...styles.alertDot, background: d.impact.startsWith('-') ? HOT.color : OK.color }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={styles.alertTitle}>{d.driver}</div>
              <div style={styles.alertDetail}>{d.signal}</div>
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, color: d.impact.startsWith('-') ? HOT.color : GOLD }}>{d.impact}</div>
          </div>
        ))}
      </div>
    </>
  );
}
